import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { apiPut, apiPost } from '../lib/api';
import { toast } from 'sonner';
import { Save, ArrowLeft, User, KeyRound, Mail, Phone, Building2, AlertTriangle, ShieldCheck } from 'lucide-react';

const CANONICAL_SUPER_ADMIN_EMAILS = ['omkar@elloraepc.com'];

export default function Profile() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
  });
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const isCanonical = CANONICAL_SUPER_ADMIN_EMAILS.includes((user?.email || '').toLowerCase());
  const inTestMode = !!user?.role_test_original;

  const saveBasics = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await apiPut('/auth/me', { name: form.name.trim(), phone: form.phone });
      await checkAuth();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e?.preventDefault?.();
    if (!pw.current || !pw.next || !pw.confirm) { toast.error('Fill all password fields'); return; }
    if (pw.next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pw.next !== pw.confirm) { toast.error('New password & confirm do not match'); return; }
    setSaving(true);
    try {
      await apiPost('/auth/change-password', { current_password: pw.current, new_password: pw.next });
      toast.success('Password changed successfully');
      setPw({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Password change failed');
    }
    setSaving(false);
  };

  const restoreSuperAdmin = async () => {
    setSaving(true);
    try {
      await apiPost('/auth/restore-super-admin', {});
      await checkAuth();
      toast.success('Restored super_admin role — reloading…');
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    }
    setSaving(false);
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl" data-testid="profile-page">
      <button onClick={() => navigate(-1)} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back</button>
      <PageHeader overline="Account" title="My Profile" subtitle="Update your personal info and password." />

      {/* Restore super-admin banner */}
      {isCanonical && user?.role !== 'super_admin' && (
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3" data-testid="restore-banner">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-sm mb-1">You're currently signed in as <span className="text-amber-700">{user?.role}</span></div>
            <p className="text-xs text-slate-700 mb-3">
              This account ({user?.email}) is the canonical super-admin. You can restore your full access at any time.
              {inTestMode && <> (Original role: <b>{user.role_test_original}</b>)</>}
            </p>
            <button onClick={restoreSuperAdmin} disabled={saving} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E] disabled:opacity-50 flex items-center gap-1.5" data-testid="restore-super-admin-btn">
              <ShieldCheck size={11} /> Restore Super Admin
            </button>
          </div>
        </div>
      )}

      {/* Account summary */}
      <div className="mt-5 bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-sm bg-[#1F7A6E] text-white flex items-center justify-center font-display font-extrabold text-2xl overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name || 'U').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg">{user?.name}</div>
            <div className="text-sm text-slate-600 flex items-center gap-1.5"><Mail size={13} /> {user?.email}</div>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="text-[10px] font-display font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-teal-50 text-[#1F7A6E]">{user?.role}</span>
              {user?.department && <span className="text-[10px] font-display font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-sm bg-slate-100 text-slate-600 flex items-center gap-1"><Building2 size={9} /> {user.department}</span>}
              {user?.employee_id && <span className="text-[10px] font-mono px-2 py-1 rounded-sm bg-slate-100 text-slate-600">{user.employee_id}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Edit basics */}
      <form onSubmit={saveBasics} className="mt-5 bg-white border border-slate-200 rounded-sm p-5">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-[#1F7A6E] mb-3 flex items-center gap-1.5"><User size={11} /> Personal Info</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Full Name *">
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-[#1F7A6E] focus:outline-none" required data-testid="profile-name-input" />
          </Field>
          <Field label="Phone">
            <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-[#1F7A6E] focus:outline-none" placeholder="+91 98765 43210" data-testid="profile-phone-input" />
          </Field>
        </div>
        <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-1"><Phone size={10} /> Email and Employee ID are managed by HR — contact them to change.</div>
        <div className="mt-4 flex justify-end">
          <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#1F7A6E] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#14504E] disabled:opacity-50" data-testid="save-profile-btn">
            <Save size={14} /> {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </form>

      {/* Change password */}
      <form onSubmit={changePassword} className="mt-5 bg-white border border-slate-200 rounded-sm p-5">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-[#1F7A6E] mb-3 flex items-center gap-1.5"><KeyRound size={11} /> Change Password</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Current Password">
            <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-[#1F7A6E] focus:outline-none" autoComplete="current-password" data-testid="pw-current" />
          </Field>
          <Field label="New Password">
            <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-[#1F7A6E] focus:outline-none" autoComplete="new-password" data-testid="pw-new" />
          </Field>
          <Field label="Confirm New Password">
            <input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-[#1F7A6E] focus:outline-none" autoComplete="new-password" data-testid="pw-confirm" />
          </Field>
        </div>
        <div className="mt-2 text-[10px] text-slate-500">Minimum 8 characters.</div>
        <div className="mt-4 flex justify-end">
          <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-sm font-display font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="change-pw-btn">
            <KeyRound size={14} /> {saving ? 'Updating…' : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-500 block mb-1">{label}</span>
      {children}
    </label>
  );
}
